import { Children, type ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { MessageTag, MessageValue } from 'react-intl';

import { DynamicYield } from '@packages/shared/src/ui/DynamicYield/DynamicYield';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';

const messages = defineMessages<{
  readonly countAndQuery: {
    readonly count: MessageValue;
    readonly query: MessageValue;
    readonly querystring: MessageTag;
  };
  readonly reducedTerms: {
    readonly reducedTerms: MessageValue;
    readonly strikethrough: MessageTag;
  };
}>({
  countAndQuery: {
    defaultMessage: '{count} Treffer für <querystring>"{query}"</querystring>',
    description: 'query reduction search query and number of results paragraph',
    id: 'searchResultSummary.countAndQuery',
  },
  reducedTerms: {
    defaultMessage: 'Es fehlt: <strikethrough>{reducedTerms}</strikethrough>',
    description: 'paragraph containing reduced search terms',
    id: 'searchResultSummary.reducedTerms',
  },
});

export type SearchResultSummaryProps = {
  count: number;
  query: string;
  /** the ignored query terms */
  ignoredSearchTerms?: string[];
  /** the result fallback type */
  isQueryReduction?: boolean;
};

const Bold = (children: ReactNode) => <span className="font-bold">{children}</span>;

const StrikeThrough = (children: ReactNode) => <span className="line-through">{children}</span>;

/**
 * Displays the search result summary and optionally reduced terms if applicable.
 * */
export const SearchResultSummary = ({
  count,
  query,
  ignoredSearchTerms,
  isQueryReduction,
}: SearchResultSummaryProps) => {
  const { dynamicYieldEnabled } = useConfig();
  const { formatMessage } = useIntl();

  return (
    <div data-testid="search-result-summary">
      {dynamicYieldEnabled && ignoredSearchTerms && (
        <>
          <DynamicYield dataDyId="serp_topbanner-desktop" className="hidden md:block" />
          <DynamicYield dataDyId="serp_topbanner-mobile" className="md:hidden" />
        </>
      )}
      <h1 className="text-size-body-base text-center">
        {formatMessage(messages.countAndQuery, {
          count,
          // Children.map needed due to some random `key` error, see https://github.com/formatjs/formatjs/issues/1467
          querystring: (...chunks) => Children.map(chunks, Bold),
          query,
        })}
      </h1>
      {isQueryReduction && ignoredSearchTerms && ignoredSearchTerms.length > 0 && (
        <div className="text-size-body-sm text-center">
          {formatMessage(messages.reducedTerms, {
            reducedTerms: ignoredSearchTerms.join(', '),
            // Children.map needed due to some random `key` error, see https://github.com/formatjs/formatjs/issues/1467
            strikethrough: (...chunks) => Children.map(chunks, StrikeThrough),
          })}
        </div>
      )}
    </div>
  );
};
